import { ModalDrawer } from '@egw/components'
import { useTranslation } from '@egw/i18n'
import { type FC } from 'react'
import {
  LinkedAccountStatus,
  type InvestmentBrokerageCompanionAccount,
  type InvestmentIRAAccount,
  type InvestmentRothIRAAccount,
  type InvestmentInheritedIRAAccount,
  type InvestmentInheritedRothIRAAccount,
  type InvestmentJointBrokerageTenantsInCommonCompanionAccount,
  type InvestmentJointBrokerageTenantsWithRightOfSurvivorshipCompanionAccount,
  type InvestmentJointBrokerageTenantsInCommonAccount,
  type InvestmentJointBrokerageTenantsWithRightOfSurvivorshipAccount,
  type BankAccountDetail,
  type InvestmentAccountDetail,
  type InvestmentJointBrokerageCommunityPropertyCompanionAccount,
  type InvestmentJointBrokerageCommunityPropertyAccount,
  type InvestmentJointBrokerageTenantsByEntiretyAccount,
  type InvestmentJointBrokerageTenantsByEntiretyCompanionAccount,
  type InvestmentBrokerageAccount,
} from '@egw/api'
import { ExclamationCircleIcon } from '@heroicons/react/20/solid'
import { type TransferListOption } from '../../types'
import { buildInternalAccounts, buildReceiveWireTransferAccounts } from '../../utils/transfer-utils/transfer-utils'

interface AccountTransferDrawerProps {
  isModalOpen: boolean
  onClose: () => void
  options?:
    | (
        | InvestmentBrokerageCompanionAccount
        | InvestmentIRAAccount
        | InvestmentRothIRAAccount
        | InvestmentInheritedIRAAccount
        | InvestmentInheritedRothIRAAccount
        | InvestmentJointBrokerageTenantsInCommonAccount
        | InvestmentJointBrokerageTenantsWithRightOfSurvivorshipAccount
        | InvestmentJointBrokerageTenantsInCommonCompanionAccount
        | InvestmentJointBrokerageTenantsWithRightOfSurvivorshipCompanionAccount
        | InvestmentJointBrokerageTenantsByEntiretyCompanionAccount
        | InvestmentJointBrokerageCommunityPropertyCompanionAccount
      )[]
    | (
        | InvestmentBrokerageAccount
        | InvestmentRothIRAAccount
        | InvestmentIRAAccount
        | InvestmentInheritedIRAAccount
        | InvestmentInheritedRothIRAAccount
        | InvestmentJointBrokerageTenantsInCommonAccount
        | InvestmentJointBrokerageTenantsWithRightOfSurvivorshipAccount
        | InvestmentJointBrokerageTenantsByEntiretyAccount
        | InvestmentJointBrokerageCommunityPropertyAccount
      )[]
    | BankAccountDetail[]
    | InvestmentAccountDetail[]
  onSelect: (option: TransferListOption) => void
  isBankAccount?: boolean
}

export const AccountTransferDrawer: FC<AccountTransferDrawerProps> = ({
  isModalOpen,
  onClose,
  options,
  onSelect,
  isBankAccount = false,
}) => {
  const { t } = useTranslation('features-transfer')

  const accounts = isBankAccount
    ? buildInternalAccounts(options as BankAccountDetail[], undefined, t)
    : buildReceiveWireTransferAccounts(options as InvestmentAccountDetail[], t)

  return (
    <ModalDrawer
      isOpen={isModalOpen}
      title={t('tabbed_account_transfer.full_transfer.to')}
      onClose={() => {
        onClose()
      }}
    >
      <div className="mb-3 mt-2 border-t border-light py-2">
        <p className="block text-sm font-semibold uppercase text-meta">{t('transfer.evergreen_accounts')}</p>
      </div>
      {accounts.map((option) => (
        <button
          key={crypto.randomUUID()}
          data-testid={`${String(option.accountType)}-${option.alias.toString()}`}
          type="button"
          id={`${String(option.accountType)}-${option.alias.toString() as unknown as string}`}
          onClick={() => {
            onSelect(option)
          }}
          className="flex w-full flex-col items-start justify-start focus-visible:outline-transparent"
        >
          <div className="flex w-full flex-row items-start align-middle justify-between gap-4 border-l-[.188rem] py-4 border-l-transparent">
            <div className="pl-2 flex flex-col items-start justify-start">
              {option.display.name ? <p className="text-body text-lg font-semibold">{option.display.name}</p> : null}
              {option.display.subtitle ? <p className="text-body text-base">{option.display.subtitle}</p> : null}
            </div>
            {option.display.linkedAccountStatus === LinkedAccountStatus.EXPIRED ? (
              <div className="flex flex-row self-center gap-1">
                <ExclamationCircleIcon className="h-4 w-4 text-alert-300 self-center" />
                <p className="text-alertErrorText self-center text-sm font-semibold">{t('transfer.expired')}</p>
              </div>
            ) : null}
          </div>
        </button>
      ))}
    </ModalDrawer>
  )
}
